import { IconPlus } from '@tabler/icons-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import { RestrictionsTypes } from '../types/RestrictionsTypes';
import AvailabilityFormRestrictions from './AvailabilityFormRestrictions';
import clsx from 'clsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { privateApi } from '../../../api/api';
import { toast } from 'sonner';
import RateFormRestrictions from './RateFormRestrictions';

interface RestrictionsTabProps {
    onClose: () => void;
}

interface Restriction {
    name: string;
    values: any;
    type?: string; // Added type for example
}

interface RestrictionSetupForm {
    restrictions: Restriction[];
}

const RestrictionsTab: React.FC<RestrictionsTabProps> = ({ onClose }) => {
    const { control, handleSubmit, setValue, getValues, watch, reset } = useForm<RestrictionSetupForm>({
        defaultValues: {
            restrictions: [],
        },
    });

    const [restrictionsSetup, setRestrictionsSetup] = useState<{ activeAccordion: string | null }>({
        activeAccordion: null,
    });

    const handleAddRestriction = () => {
        setRestrictionsSetup({ activeAccordion: `${getValues('restrictions').length}` });
        const currentRestrictions = getValues('restrictions');
        setValue('restrictions', [...currentRestrictions, { name: '', values: '', type: '' }]);
    };

    const handleRemoveRestrictionSetup = (index: number) => {
        const currentRestrictions = getValues('restrictions');
        const updatedRestrictions = currentRestrictions.filter((_, i) => i !== index);
        setValue('restrictions', updatedRestrictions);
    };

    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        mutationFn: (data: RestrictionSetupForm) => {
            const formattedValues = data.restrictions.map((restriction) => {
                if (restriction.type === 'availability') {
                    if (!restriction.values.is_checked_availability) {
                        const errorMsg = 'There are restrictions that have not been checked for availability';
                        throw new Error(errorMsg);
                    }

                    return {
                        type: 'availability',
                        availability: restriction.values.value,
                        date_from: restriction.values.date_from,
                        date_to: restriction.values.date_to,
                        room_type_id: restriction.values.room_type_id.value,
                        is_checked_availability: restriction.values.is_checked_availability,
                    };
                } else if (restriction.type === 'rate') {
                    return {
                        type: 'rate',
                        rate: restriction.values.value,
                        date_from: restriction.values.date_from,
                        date_to: restriction.values.date_to,
                        rate_plan_id: restriction.values.rate_plan_id.value,
                    }
                } else {
                    const errMsg = 'There are restrictions have invalid type';
                    throw new Error(errMsg);
                }
            });
            return privateApi.post('/v4/update-availability-restriction', {
                values: formattedValues,
            });
        },
        onSuccess: async () => {
            // 'price-adjustment' WAJIB ikut supaya baris DPM ikut ter-update —
            // queryKey-nya (['price-adjustment', filter]) tidak mengandung key lain di sini.
            await queryClient.refetchQueries({
                predicate: (query) => query.queryKey.some((key) => ['rooms', 'rates', 'availability', 'restrictions', 'price-adjustment'].includes(key as string))
            })

            reset();
            onClose();

            toast.success('Restrictions Saved', {
                id: 'save-restrictions',
            });
        },
        onError: (error) => {
            console.log('error', error);
            toast.error((error as any)?.response?.data?.message || error.message || 'Failed to save restrictions', {
                id: 'save-restrictions',
            });
        },
    })

    const onSubmit = useCallback((data: RestrictionSetupForm) => {
        toast.loading('Saving Restrictions...', {
            id: 'save-restrictions',
        });
        mutate(data);
    }, [mutate]);

    useEffect(() => {
        return () => {
            reset();
        }
    }, [onClose]);

    return (
        <form
            className="d-flex flex-column"
            style={{ height: '75vh' }}
            onSubmit={handleSubmit(onSubmit)}
        >
            <div className="action-sheet-content flex-grow-1">
                <Controller
                    name="restrictions"
                    control={control}
                    render={({ field }) => (
                        <>
                            {field.value.map((_restriction, index) => (
                                <Accordion
                                    key={index}
                                    activeKey={restrictionsSetup.activeAccordion === `${index}` ? `${index}` : undefined}
                                    onSelect={() => setRestrictionsSetup({ activeAccordion: index.toString() })}
                                    flush
                                >
                                    <Accordion.Item eventKey={`${index}`}>
                                        <Accordion.Header>
                                            <div className="d-flex justify-content-between align-items-center w-100">
                                                <span>Restriction Setup #{index + 1}</span>
                                                <div className='btn btn-danger btn-sm me-2' onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveRestrictionSetup(index);
                                                }}>Delete</div>

                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <RestrictionsSetup field={`restrictions.${index}`} control={control} watch={watch} position={index} setValue={setValue} />
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            ))}
                        </>
                    )}
                />
                <div className='row mb-3 align-items-center'>
                    <div className={clsx('d-flex pt-3 justify-content-center', watch('restrictions').length > 0 && 'border-top')}>
                        <button type='button' className='p-0 border-0 bg-white d-flex align-items-center' onClick={() => handleAddRestriction()}>
                            <div className='border-1 rounded-circle bg-primary'>
                                <IconPlus size={24} className='text-white' />
                            </div>
                            <span className='ms-2'>Add Restriction Setup</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Submit Button */}
            <div className="form-group mt-auto d-grid">
                <button type="submit" className="btn btn-primary btn-block" disabled={isPending}>
                    {isPending ? 'Saving...' : 'Save'}
                </button>
            </div>
        </form>
    );
};

export default RestrictionsTab;

interface RestrictionsSetupProps {
    field: any; // Use `any` for simplicity; replace with correct typing if needed
    control: any; // Use `any` for simplicity; replace with correct typing if needed
    watch: any; // Use `any` for simplicity; replace with correct typing if needed
    position: number;
    setValue?: any; // Use `any` for simplicity; replace with correct typing if needed
}

const RestrictionsSetup: React.FC<RestrictionsSetupProps> = ({ field, control, watch, position, setValue }) => {
    return (
        <div>
            <div className="row mb-3 align-items-center">
                <label className="col-sm-4 col-form-label d-flex justify-content-end">
                    Type:
                </label>
                <div className="col-sm-8">
                    <Controller
                        name={`${field}.type`}
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...field}
                                value={RestrictionsTypes.find((option) => option.value === field.value)}
                                options={RestrictionsTypes}
                                placeholder="Select restriction type"
                                onChange={(selectedOption) => {
                                    if (selectedOption) {
                                        field.onChange(selectedOption.value);
                                    }
                                }}
                            />
                        )}
                    />
                </div>
            </div>
            {watch(`${field}.type`) === 'availability' && (
                <AvailabilityFormRestrictions field={field} control={control} watch={watch} position={position} setValue={setValue} />
            )}
            {watch(`${field}.type`) === 'rate' && (
                <RateFormRestrictions field={field} control={control} watch={watch} setValue={setValue} />
            )}
        </div>
    );
};
